// Activity-log labels for the admin console. The customer sites record what
// an event was about in its metadata: contentType for anything opened (speed
// map, race results, track forecast), control + value for toggles, target for
// clicks. Without it every expand reads the same.

type Meta = Record<string, unknown>;

const words = (v: unknown): string | null =>
  typeof v === 'string' || typeof v === 'number' ? String(v).replace(/_/g, ' ') : null;

/** "content_expand_v2" -> "content expand v2" */
export const eventLabel = (eventType: string): string => eventType.replace(/_/g, ' ');

/** What an event was about: "speed map", "tips view: compact", "date nav: next", "classic → new". */
export function activityDetail(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const m = metadata as Meta;
  if (m.contentType) return words(m.contentType);
  if (m.control) return [words(m.control), words(m.value)].filter(Boolean).join(': ');
  if (m.target) return [words(m.target), words(m.direction) ?? words(m.label)].filter(Boolean).join(': ');
  if (m.from && m.to) return `${words(m.from)} → ${words(m.to)}`;
  return null;
}

/** Event plus detail, counted and sorted most-used first, e.g. "content expand v2 · speed map". */
export function actionBreakdown(activities: { event_type: string; metadata: unknown }[]): { label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const a of activities) {
    const detail = activityDetail(a.metadata);
    const label = detail ? `${eventLabel(a.event_type)} · ${detail}` : eventLabel(a.event_type);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}
